import axios from 'axios';
import { setAlert } from './alert';
import { 
    GET_DELIVERY_MANAGEMENT,
    DELIVERY_LOADING,
    ASSIGN_DELIVERY_AGENT,
    UPDATE_DELIVERY_STATUS,
    DELIVERY_ERROR,
    GET_DELIVERY_AGENTS
} from './types';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const FILTER_KEYS = ['search', 'agentName', 'startDate', 'endDate'];

/**
 * Get one status tab of delivery management data with server-side pagination and filters
 * @param {Object} options
 * @param {string} [options.status] - pending, scheduled, assigned, in_progress, delivered, cancelled or failed
 * @param {number} [options.page] - Page number
 * @param {number} [options.limit] - Page size
 * @param {string} [options.search] - Customer name/phone, transaction ID, address or product
 * @param {string} [options.agentName] - Assigned agent name (partial match)
 * @param {string} [options.startDate] - Request date from (YYYY-MM-DD)
 * @param {string} [options.endDate] - Request date to (YYYY-MM-DD)
 */
export const getDeliveryManagement = ({ status = 'pending', page = 1, limit = 20, ...filters } = {}) => async dispatch => {
    try {
        dispatch({ type: DELIVERY_LOADING });

        const params = { status, page, limit };
        FILTER_KEYS.forEach(key => {
            if (filters[key]) params[key] = filters[key];
        });

        const res = await axios.get(`${API_URL}/delivery/operations/user-delivery-management`, { params });
        if (res.data.success) {
            const { deliveries, counts, total, pagination } = res.data.data;
            dispatch({
                type: GET_DELIVERY_MANAGEMENT,
                payload: { deliveries, counts, total, pages: pagination.pages }
            });
        } else {
            dispatch({ 
                type: DELIVERY_ERROR,
                payload: res.data.error || 'Failed to fetch delivery data'
            });
            dispatch(setAlert(res.data.error || 'Failed to fetch delivery data', 'danger'));
        }
    } catch (err) {
        console.error('Error fetching delivery management data:', err);
        dispatch({ 
            type: DELIVERY_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert(err.response?.data?.error || 'Failed to fetch delivery management data', 'danger'));
    }
};

/**
 * Get all available delivery agents
 */
export const getDeliveryAgents = (id) => async dispatch => {
    try {
        // Add id as query param
        const res = await axios.get(`${API_URL}/delivery/operations/available-agents?id=${id}`);
        
        if (res.data.success) {
            dispatch({
                type: GET_DELIVERY_AGENTS,
                payload: res.data.agents
            });
        } else {
            dispatch(setAlert(res.data.error || 'Failed to fetch delivery agents', 'danger'));
        }
    } catch (err) {
        console.error('Error fetching delivery agents:', err);
        dispatch(setAlert('Failed to fetch delivery agents', 'danger'));
    }
};

/**
 * Assign a delivery agent to a pending delivery
 * @param {string} agentId - The ID of the agent to assign
 * @param {string} transactionId - The ID of the transaction to assign
 * @param {string} agentType - The type of agent to assign
 * @param {number} totalTime - The total time to assign
 * @param {boolean} agentNeedsRefill - Whether the agent needs a refill
 */
export const assignDeliveryAgent = (transactionId, agentId, agentType, totalTime, agentNeedsRefill = false ) => async dispatch => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = JSON.stringify({ agentId, transactionId, agentType, totalTime, agentNeedsRefill });
        
        const res = await axios.post(`${API_URL}/delivery/operations/assign-delivery`, body, config);
        
        if (res.data.success) {
            dispatch({
                type: ASSIGN_DELIVERY_AGENT,
                payload: {
                    transactionId,
                    updatedDelivery: res.data.delivery
                }
            });
            dispatch(setAlert('Delivery agent assigned successfully', 'success'));
        } else {
            dispatch(setAlert(res.data.error || 'Failed to assign delivery agent', 'danger'));
        }
    } catch (err) {
        console.error('Error assigning delivery agent:', err);
        dispatch(setAlert('Failed to assign delivery agent', 'danger'));
    }
};

/**
 * Update the status of a delivery
 * @param {string} deliveryId - The ID of the delivery to update
 * @param {string} status - The new status (in-progress, delivered, cancelled, failed)
 * @param {object} additionalData - Any additional data to include in the update
 */
export const updateDeliveryStatus = (deliveryId, status, additionalData = {}) => async dispatch => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = JSON.stringify({ 
            status,
            ...additionalData
        });
        
        const res = await axios.put(`${API_URL}/delivery/${deliveryId}/status`, body, config);
        
        if (res.data.success) {
            dispatch({
                type: UPDATE_DELIVERY_STATUS,
                payload: {
                    deliveryId,
                    updatedDelivery: res.data.delivery
                }
            });
            
            const statusMessages = {
                'in-progress': 'Delivery marked as in progress',
                'delivered': 'Delivery marked as delivered',
                'cancelled': 'Delivery has been cancelled',
                'failed': 'Delivery has been marked as failed'
            };
            
            dispatch(setAlert(statusMessages[status] || 'Delivery status updated', 'success'));
        } else {
            dispatch(setAlert(res.data.error || 'Failed to update delivery status', 'danger'));
        }
    } catch (err) {
        console.error('Error updating delivery status:', err);
        dispatch(setAlert('Failed to update delivery status', 'danger'));
    }
};